import type { ComponentProps, ReactNode } from 'react';

import { FormLabel } from '@playbook/atoms/form-label';
import { DropdownTrigger } from '@playbook/atoms/inputs/dropdown-trigger';
import type { DropdownTriggerSize } from '@playbook/atoms/inputs/dropdown-trigger';
import { MenuItem } from '@playbook/atoms/menu-item';
import { ContextMenu } from '@playbook/modules/context-menu';
import type { ContextMenuAppearFrom } from '@playbook/modules/context-menu';
import { cn } from '@playbook/utils';

import css from './styles.module.css';

const DefaultDropdownSize: DropdownTriggerSize = 'md' as const;

const DefaultDropdownAppearFrom: ContextMenuAppearFrom = 'bottomLeft' as const;

/** One choice in the list. `label` is what it reads as; `value` is what it is. */
export interface DropdownOption {
	value: string;
	label: string;
	disabled?: boolean;
}

/**
 *	Typed for the element they land on. `{...props}` goes to the trigger, not
 *	to the wrapper — a caller's `onClick` means the control, as it does on
 *	`atoms/inputs/text-area`.
 */
type DropdownEventHandlers = {
	[K in keyof ComponentProps<'button'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'button'>[K];
};

interface DropdownDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface DropdownProperties
	extends DropdownEventHandlers, DropdownDataAttributes {
	size?: DropdownTriggerSize;
	/** Where the list opens from the trigger, as on `modules/context-menu`. */
	appearFrom?: ContextMenuAppearFrom;
	disabled?: boolean;
	required?: boolean;
	optional?: boolean;

	name: string;

	/** The chosen option's `value`, or nothing chosen yet. */
	value?: string;

	options: DropdownOption[];

	/**
	 *	Shown until something is chosen, and the name the list falls back to
	 *	when there is no visible label. Required, since what to choose is the
	 *	caller's to say: no single word fits every list.
	 */
	placeholder: string;

	label?: string;
	description?: string;
	help?: string;

	onValueChange: (value: string) => void;
}

/**
 *	A labelled control that opens a list and takes one choice from it.
 *
 *	The pieces are all already entities: the label above it, the closed
 *	trigger, and the context menu of options. This arranges them.
 *
 *	The options are ordinary buttons reached with Tab, not a `role="listbox"`
 *	with arrow keys. That is the same call `compositions/menu` documents and
 *	makes deliberately: the ARIA listbox pattern promises arrow keys move
 *	between options and Tab leaves, and promising that without implementing it
 *	misleads exactly the people who depend on it. The control is fully operable
 *	from the keyboard either way.
 */
export function Dropdown({
	size = DefaultDropdownSize,
	appearFrom = DefaultDropdownAppearFrom,
	disabled = false,
	required = false,
	optional = false,

	name,
	value,
	options,
	placeholder,

	label,
	description,
	help,

	onValueChange,

	...props
}: DropdownProperties): ReactNode {
	const className: string = cn(css.field, size === 'sm' && css.sm);

	const chosen: DropdownOption | undefined = options.find(
		(option) => option.value === value
	);

	/**
	 *	The words the trigger shows, and the name the list is announced by. A
	 *	`value` with no matching option shows the placeholder rather than the
	 *	raw value — a stale id is not something to read out.
	 */
	const listLabel: string = label ?? placeholder;

	return (
		<div data-size={size} className={className}>
			{label && (
				<FormLabel
					htmlFor={name}
					size={size}
					label={label}
					description={description}
					help={help}
					optional={optional}
					required={required}
				/>
			)}

			<ContextMenu
				appearFrom={appearFrom}
				label={listLabel}
				size={size}
				inheritTriggerWidth={true}
				renderTriggerWith={(popoverTarget, open) => (
					/**
					 *	`aria-labelledby` names the trigger with the label's id followed
					 *	by its own, so the field is announced as "Team, Under 15s" rather
					 *	than one or the other. `htmlFor` alone would name an `<input>`
					 *	and does not name a `<button>`, whose accessible name comes from
					 *	its own contents.
					 */
					<DropdownTrigger
						{...props}
						size={size}
						disabled={disabled}
						name={name}
						placeholder={placeholder}
						value={chosen?.label}
						popoverTarget={popoverTarget}
						aria-haspopup="true"
						aria-expanded={open}
						aria-labelledby={label ? `${name}-label ${name}` : undefined}
						aria-describedby={description ? `${name}-description` : undefined}
					/>
				)}>
				{options.map((option) => (
					<MenuItem
						key={option.value}
						label={option.label}
						variant={option.value === value ? 'selected' : 'default'}
						disabled={option.disabled}
						onClick={() => onValueChange(option.value)}
					/>
				))}
			</ContextMenu>

			{/**
			 *	What a form submits. The trigger is a `<button>`, which carries no
			 *	value of its own, so without this `name` would be a label wire and
			 *	nothing else.
			 */}
			<input type="hidden" name={name} value={value ?? ''} />
		</div>
	);
}
